import { useEffect, useState } from 'react'
import useSWR from 'swr'
import {
  SocialEstimateRequest,
  SocialEstimateResponse,
} from '@digital-twins/shared-types'

import { useSocialListeningApi } from './SocialListeningApiContext'
import { SOCIAL_ESTIMATE_DEBOUNCE_MS } from './socialCredits'

export const SOCIAL_ESTIMATE_SWR_KEY = 'social-listening/estimate'

/** The dialog's draft (`toSocialEstimateRequest`): the server plans and prices it as the run would. */
interface UseSocialEstimateInput {
  request: SocialEstimateRequest
  /** False skips the request (step not shown, no channel picked). */
  shouldLoad: boolean
}

interface UseSocialEstimateResult {
  /** Last answer, kept while a changed selection refetches; null before the first or after an error. */
  estimate: SocialEstimateResponse | null
  /** First load: nothing to show yet. */
  isLoading: boolean
  /** A newer selection is debouncing or loading: show the previous values dimmed. */
  isRefreshing: boolean
  hasError: boolean
}

type EstimateKey = readonly [string, string]

/** One string per draft (the request as JSON), so equal drafts share a key. */
function estimateKeyId(input: UseSocialEstimateInput): string | null {
  if (!input.shouldLoad || input.request.channels.length === 0) {
    return null
  }
  return JSON.stringify(input.request)
}

function keyFromId(id: string | null): EstimateKey | null {
  if (id === null) {
    return null
  }
  return [SOCIAL_ESTIMATE_SWR_KEY, id] as const
}

function requestFromId(id: string): SocialEstimateRequest {
  return JSON.parse(id) as SocialEstimateRequest
}

/**
 * The dialog's server estimate (E2): SWR keyed by the draft request, the key
 * debounced SOCIAL_ESTIMATE_DEBOUNCE_MS so a run of picks
 * makes one request, and the previous answer kept on screen while the next
 * loads (`keepPreviousData`). An error hides the rows and never blocks Next
 * or Create (the caller decides).
 */
export function useSocialEstimate(input: UseSocialEstimateInput): UseSocialEstimateResult {
  const socialListeningApi = useSocialListeningApi()
  const liveId = estimateKeyId(input)
  const [debouncedId, setDebouncedId] = useState<string | null>(liveId)

  useEffect(() => {
    if (liveId === debouncedId) {
      return undefined
    }
    const timer = setTimeout(() => setDebouncedId(liveId), SOCIAL_ESTIMATE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [liveId, debouncedId])

  const { data, error, isLoading, isValidating } = useSWR<SocialEstimateResponse>(
    keyFromId(debouncedId),
    ([, id]: EstimateKey) => socialListeningApi.getSocialEstimate(requestFromId(id)),
    { keepPreviousData: true, revalidateOnFocus: false },
  )

  const isPending = liveId !== debouncedId
  const estimate = error ? null : (data ?? null)

  return {
    estimate,
    isLoading: estimate === null && !error && liveId !== null && (isLoading || isPending),
    isRefreshing: estimate !== null && (isPending || isValidating),
    hasError: Boolean(error),
  }
}
